import { en, ka } from '@skillarea/locale-skillarea';
import {
  bindRequestLocaleGetter,
  DEFAULT_LOCALE,
  normalizeLocale,
} from '@skillarea/lib-shared';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { resolveClientLocale } from '~/lib/locale';

function setDocumentLang(locale: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.lang = locale;
}

export function getInitialLocale(): string {
  if (typeof document !== 'undefined') {
    const fromHtml = normalizeLocale(document.documentElement.lang);
    if (fromHtml) {
      return fromHtml;
    }

    return resolveClientLocale(null);
  }

  return DEFAULT_LOCALE;
}

const i18nInitOptions = {
  fallbackLng: DEFAULT_LOCALE,
  debug: import.meta.env.DEV,
  nsSeparator: ':',
  resources: {
    en,
    ka,
  },
  interpolation: { escapeValue: false },
  react: {
    useSuspense: false,
  },
} as const;

void i18next.use(initReactI18next).init({
  ...i18nInitOptions,
  lng: getInitialLocale(),
});

function getClientRequestLocale(): string {
  return (
    normalizeLocale(i18next.resolvedLanguage ?? i18next.language) ??
    DEFAULT_LOCALE
  );
}

bindRequestLocaleGetter(getClientRequestLocale);

export function ensureI18nLocale(locale: string): string {
  const resolved = normalizeLocale(locale) ?? DEFAULT_LOCALE;

  if (i18next.resolvedLanguage !== resolved) {
    i18next.changeLanguage(resolved);
  }

  setDocumentLang(resolved);
  return resolved;
}

export function applyLocale(locale: string): string {
  return ensureI18nLocale(locale);
}

export default i18next;
